/**
 * Super admin "View as user" (TRUS-747) — the browser side.
 *
 * Starting a session asks the API for a short-lived token whose subject is the
 * chosen user (staff or member-portal) and swaps it in, so every screen loads
 * exactly as it does for them. The admin's own credentials are set aside in
 * localStorage and put back on exit. The impersonation token has no refresh
 * token on purpose: when it expires, or the server ends the session, the
 * browser drops back to the admin.
 *
 * Nothing here enforces anything. The API does (middleware/impersonation_guard.py):
 * it re-checks the session on every request, refuses what the mode does not
 * allow, and records it all. This module only keeps the browser consistent.
 */
import { getWeaveinApiUrl } from '@/src/config';

export type ImpersonationMode = 'read_only' | 'read_write';

export interface ImpersonationState {
  sessionId: number;
  mode: ImpersonationMode;
  ticketKey: string;
  classification: string;
  classificationLabel: string;
  /** epoch ms */
  expiresAt: number;
  actorName: string | null;
  actorEmail: string | null;
  target: {
    id: number;
    name: string | null;
    email: string | null;
    kind: 'staff' | 'member';
    role: string | null;
    memberId: number | null;
  };
  organization: { id: number; name: string };
  /** Where to send the admin back to on exit. */
  returnPath: string;
}

export interface StartImpersonationInput {
  targetUserId: number;
  cboId: number;
  /** Blank: the API creates a Jira ticket for the session. */
  ticketKey?: string;
  classification: string;
  reason: string;
  mode: ImpersonationMode;
  confirmReadWrite?: boolean;
}

const STATE_KEY = 'weavein_impersonation';
const STASH_KEY = 'weavein_impersonation_stash';

// Everything a sign-in writes, staff or member. Set aside on start, restored on exit.
const SESSION_KEYS = [
  'auth_token',
  'refresh_token',
  'token_expires_at',
  'session_start',
  'user',
  'active_cbo_id',
  'sidebar_preview_role',
  'member_token',
  'member_data',
  'member_organizations',
] as const;

export const IMPERSONATION_CHANGED_EVENT = 'impersonation:changed';
export const IMPERSONATION_BLOCKED_EVENT = 'impersonation:blocked';

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function getImpersonation(): ImpersonationState | null {
  if (typeof window === 'undefined') return null;
  return readJson<ImpersonationState>(STATE_KEY);
}

export function isImpersonating(): boolean {
  return getImpersonation() !== null;
}

/**
 * The window to navigate. "View as" can be clicked inside a tab of the main
 * window's tab strip, which is a same-origin iframe: navigating that frame
 * would load the whole app (sidebar, banner and all) inside the outer app.
 * Starting and ending a session always reloads the top window instead.
 */
function topLocation(): Location {
  try {
    if (window.top && window.top !== window.self) return window.top.location;
  } catch {
    /* cross-origin parent: stay in this window */
  }
  return window.location;
}

/** The token the current session (staff or member) is using. */
function currentToken(): string | null {
  return localStorage.getItem('auth_token') || localStorage.getItem('member_token');
}

async function errorDetail(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.detail === 'string') return body.detail;
    if (Array.isArray(body?.detail) && body.detail[0]?.msg) return String(body.detail[0].msg);
  } catch {
    /* not JSON */
  }
  return fallback;
}

/**
 * Start a session and reload into the user's view. Throws with a readable
 * message on refusal (org opted out, not a super admin, bad ticket, ...).
 */
export async function startImpersonation(input: StartImpersonationInput): Promise<void> {
  if (isImpersonating()) {
    throw new Error('Exit the current View as user session first.');
  }
  const token = currentToken();
  if (!token) throw new Error('You are not signed in.');

  const res = await fetch(getWeaveinApiUrl('/api/impersonation/sessions'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      target_user_id: input.targetUserId,
      cbo_id: input.cboId,
      ticket_key: input.ticketKey?.trim() || null,
      classification: input.classification,
      reason: input.reason,
      mode: input.mode,
      confirm_read_write: !!input.confirmReadWrite,
    }),
  });
  if (!res.ok) {
    throw new Error(await errorDetail(res, `Could not start the session (HTTP ${res.status}).`));
  }
  const data = await res.json();

  // Set the admin's own sign-in aside, exactly as it is.
  const stash: Record<string, string | null> = {};
  for (const key of SESSION_KEYS) stash[key] = localStorage.getItem(key);
  localStorage.setItem(STASH_KEY, JSON.stringify(stash));
  for (const key of SESSION_KEYS) localStorage.removeItem(key);

  const expiresAt = Date.parse(data.expires_at);
  const state: ImpersonationState = {
    sessionId: data.session.id,
    mode: data.session.mode,
    ticketKey: data.session.ticket_key,
    classification: data.session.classification,
    classificationLabel: data.session.classification_label,
    expiresAt,
    actorName: data.session.actor_name ?? null,
    actorEmail: data.session.actor_email ?? null,
    target: {
      id: data.target.id,
      name: data.target.name ?? null,
      email: data.target.email ?? null,
      kind: data.target.kind,
      role: data.target.role ?? null,
      memberId: data.target.member_id ?? null,
    },
    organization: { id: data.organization.id, name: data.organization.name },
    returnPath: topLocation().pathname + topLocation().search,
  };
  localStorage.setItem(STATE_KEY, JSON.stringify(state));

  // Pinned to this org: the API refuses any other X-CBO-ID.
  localStorage.setItem('active_cbo_id', String(data.organization.id));
  if (state.target.kind === 'member') {
    localStorage.setItem('member_token', data.access_token);
    if (data.member) localStorage.setItem('member_data', JSON.stringify(data.member));
    localStorage.setItem('member_organizations', JSON.stringify(data.organizations || []));
    topLocation().href = '/member/dashboard';
  } else {
    localStorage.setItem('auth_token', data.access_token);
    localStorage.setItem('token_expires_at', String(expiresAt));
    localStorage.setItem('session_start', String(Date.now()));
    topLocation().href = '/';
  }
}

let exiting = false;

/**
 * End the session on the server (best effort) and put the admin's own sign-in
 * back. Safe to call more than once and from any 401 handler.
 */
export async function exitImpersonation(reason: string = 'exited'): Promise<void> {
  const state = getImpersonation();
  if (!state || exiting) return;
  exiting = true;

  const token = currentToken();
  if (token) {
    try {
      await fetch(getWeaveinApiUrl(`/api/impersonation/sessions/${state.sessionId}/end`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      });
    } catch {
      /* the session also ends on its own at expiry */
    }
  }

  const stash = readJson<Record<string, string | null>>(STASH_KEY) || {};
  for (const key of SESSION_KEYS) localStorage.removeItem(key);
  for (const key of SESSION_KEYS) {
    const value = stash[key];
    if (value !== null && value !== undefined) localStorage.setItem(key, value);
  }
  localStorage.removeItem(STASH_KEY);
  localStorage.removeItem(STATE_KEY);
  console.info(`View as user session ${state.sessionId} ended (${reason})`);
  exiting = false;
  topLocation().href = state.returnPath || '/';
}

/** Called by API layers when the server refuses something under impersonation. */
export function reportImpersonationBlocked(detail: { message?: string; rule?: string; path?: string }): void {
  if (typeof window === 'undefined') return;
  // A page in a tab-strip iframe has no banner of its own; tell the top window's.
  let target: Window = window;
  try {
    if (window.top && window.top !== window.self) target = window.top;
  } catch {
    /* cross-origin parent */
  }
  const Ctor = (target as unknown as { CustomEvent: typeof CustomEvent }).CustomEvent || CustomEvent;
  target.dispatchEvent(new Ctor(IMPERSONATION_BLOCKED_EVENT, { detail }));
}

/**
 * Inspect an error response body. Returns true when it was about the
 * impersonation session (so the caller should not run its normal handling).
 */
export function handleImpersonationResponse(status: number, body: any, path?: string): boolean {
  if (!isImpersonating() || !body || typeof body !== 'object') return false;
  if (status === 401 && (body.code === 'impersonation_ended' || body.code === 'impersonation_unverified')) {
    void exitImpersonation(body.reason || 'ended');
    return true;
  }
  if (status === 403 && body.code === 'impersonation_blocked') {
    reportImpersonationBlocked({ message: body.detail, rule: body.rule, path });
    return true;
  }
  return false;
}
